import type { RtoTermDefinition } from "@/lib/rto/rtoTermSettings";
import type { FetchResult } from "./opsPaperworkClientBase";
import { BASE, opsFetch, q } from "./opsPaperworkClientBase";

export async function listOpsIntegrationEvents(dealId?: string): Promise<FetchResult<{ ok: true; events: unknown[] }>> {
  return opsFetch(q("integration-events", dealId ? { deal_id: dealId } : undefined), { method: "GET" });
}

export type OpsSettingsGetPayload = {
  ok: true;
  settings: Record<string, unknown>;
  manufacturer_options?: Array<{ id: string; name: string; on_platform?: boolean }>;
  effective_rto_term_definitions?: unknown[];
  effective_sales_console_rto_title?: string | null;
};

/**
 * GET settings returns `effective_rto_term_definitions` on the JSON envelope (inherited manufacturer terms),
 * not nested under `settings` (which holds the org row, often empty `rto_term_definitions` for subscribers).
 */
export function rtoTermDefinitionsFromOpsSettingsResponse(data: Record<string, unknown>): RtoTermDefinition[] {
  const settings = (data.settings ?? {}) as Record<string, unknown>;
  const raw =
    data.effective_rto_term_definitions ??
    settings.effective_rto_term_definitions ??
    settings.rto_term_definitions;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x) => x && typeof x === "object") as RtoTermDefinition[];
}

export async function getOpsSettings(): Promise<FetchResult<OpsSettingsGetPayload>> {
  return opsFetch(q("settings"), { method: "GET" });
}

export async function patchOpsSettings(body: Record<string, unknown>): Promise<FetchResult<{ ok: true; settings: unknown }>> {
  return opsFetch(q("settings"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function rotateOpsInventoryHostWebhookToken(): Promise<
  FetchResult<{ ok: true; token: string; webhook_path: string }>
> {
  return opsFetch(q("rotate-inventory-host-webhook-token"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
}

export async function createOpsDeal(body: Record<string, unknown>): Promise<FetchResult<{ ok: true; deal: unknown }>> {
  return opsFetch(q("deal"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patchOpsDeal(body: Record<string, unknown>): Promise<FetchResult<{ ok: true; deal: unknown }>> {
  return opsFetch(q("deal"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function submitOpsDealToBuild(dealId: string): Promise<
  FetchResult<{ ok: true; already: boolean; deal_id: string; unit_id: string; serial: string | null }>
> {
  return opsFetch(q("submit-to-build"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deal_id: dealId }),
  });
}

export async function deleteOpsDeal(id: string): Promise<FetchResult<{ ok: true }>> {
  const p = new URLSearchParams({ ops: "deal", id });
  return opsFetch(`${BASE}?${p}`, { method: "DELETE" });
}

export async function seedOpsTemplates(): Promise<FetchResult<{ ok: true; templates: unknown[] }>> {
  return opsFetch(q("seed-templates"), { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
}

export async function seedOpsPaperwork(dealId: string): Promise<FetchResult<{ ok: true; deal: unknown; paperwork_items: unknown[] }>> {
  return opsFetch(q("seed-paperwork"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deal_id: dealId }),
  });
}

export async function patchOpsPaperworkItem(body: Record<string, unknown>): Promise<FetchResult<{ ok: true; item: unknown }>> {
  return opsFetch(q("paperwork-item"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function markAllOpsNotificationsRead(): Promise<FetchResult<{ ok: true }>> {
  return opsFetch(q("notifications-mark-read"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ all: true }),
  });
}

export async function markOpsNotificationsReadByIds(ids: string[]): Promise<FetchResult<{ ok: true }>> {
  return opsFetch(q("notifications-mark-read"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
}

export async function acknowledgeOpsNotification(body: {
  id: string;
}): Promise<FetchResult<{ ok: true; notification_id: string; acknowledged_at: string }>> {
  return opsFetch(q("notifications-ack"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function listOpsActionableAcknowledgements(): Promise<FetchResult<{ ok: true; items: unknown[] }>> {
  return opsFetch(q("notifications-actionable"), { method: "GET" });
}

export async function listOpsReminders(): Promise<FetchResult<{ ok: true; reminders: unknown[] }>> {
  return opsFetch(q("reminders"), { method: "GET" });
}

export async function createOpsReminder(body: {
  title: string;
  body?: string;
  scope: "lead" | "user";
  due_at?: string | null;
  lead_id?: string | null;
  assignee_user_id?: string | null;
}): Promise<FetchResult<{ ok: true; reminder: unknown }>> {
  return opsFetch(q("reminders"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patchOpsReminder(body: {
  id: string;
  action: "dismiss" | "resolve" | "reassign";
  assignee_user_id?: string | null;
  reason?: string;
}): Promise<FetchResult<{ ok: true; reminder: unknown }>> {
  return opsFetch(q("reminders"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export type WorkplaceCommsDiagnosticsData = {
  ok: true;
  generated_at: string;
  flood_policy: string;
  ack_blocking_enabled: boolean;
  pending_required_ack_count: number;
  blocked_checklist_user_count: number;
  ack_backlog_by_user: { user_id: string; pending_ack_count: number }[];
  flood_guardrails_enabled: boolean;
  flood_kind_limits: Record<string, number>;
};

export type OversightDeepLink = {
  lead_id?: string;
  deal_id?: string;
  thread_id?: string;
  task_id?: string;
  notification_id?: string;
};

export type WorkplaceCommsOversightData = {
  ok: true;
  generated_at: string;
  flood_policy: string;
  filters: {
    from: string;
    to: string;
    severity: string;
    kind: string | null;
    user_id: string | null;
  };
  diagnostics: Omit<WorkplaceCommsDiagnosticsData, "ok" | "generated_at" | "flood_policy">;
  blocked_checklist_users: Array<{
    user_id: string;
    display_name: string;
    pending_ack_count: number;
    severity: string;
  }>;
  ack_backlog_by_user: Array<{
    user_id: string;
    display_name: string;
    pending_ack_count: number;
    severity: string;
  }>;
  recent_nudges: Array<{
    id: string;
    kind: string;
    title: string;
    body: string;
    user_id: string;
    display_name: string;
    severity: string;
    created_at: string;
    read_at: string | null;
    requires_ack: boolean;
    acknowledged_at: string | null;
    deep_link: OversightDeepLink;
  }>;
  hygiene_resolutions: Array<{
    id: string;
    lead_id: string;
    lead_label: string;
    actor_user_id: string | null;
    actor_display_name: string;
    action: string;
    reason: string | null;
    cadence_days: number | null;
    severity: string;
    created_at: string;
    deep_link: OversightDeepLink;
  }>;
};

export async function getWorkplaceCommsDiagnostics(): Promise<FetchResult<WorkplaceCommsDiagnosticsData>> {
  return opsFetch(q("workplace-comms-diagnostics"), { method: "GET" });
}

export async function getWorkplaceCommsOversight(params?: {
  from?: string;
  to?: string;
  severity?: string;
  kind?: string;
  user_id?: string;
}): Promise<FetchResult<WorkplaceCommsOversightData>> {
  const extra: Record<string, string> = {};
  if (params?.from) extra.from = params.from;
  if (params?.to) extra.to = params.to;
  if (params?.severity) extra.severity = params.severity;
  if (params?.kind) extra.kind = params.kind;
  if (params?.user_id) extra.user_id = params.user_id;
  return opsFetch(q("workplace-comms-oversight", Object.keys(extra).length ? extra : undefined), {
    method: "GET",
  });
}

/** Admin / owner / dev / platform_admin / platform_dev — server checks `canVoid` roles. */
export async function createTestOpsNotification(): Promise<FetchResult<{ ok: true }>> {
  return opsFetch(q("notifications-test"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
}

/** Trainer/marketing/admin: broadcast org nudge to all active org members (`sales_checklist_nudge` notifications). */
export async function postChecklistBroadcastNudge(body: {
  title: string;
  body: string;
}): Promise<FetchResult<{ ok: true; recipient_count: number; sent_at: string }>> {
  return opsFetch(q("notifications-checklist-broadcast"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function listOpsTasks(box: "inbox" | "sent"): Promise<FetchResult<{ ok: true; tasks: unknown[] }>> {
  return opsFetch(q("tasks", { box }), { method: "GET" });
}

export async function createOpsTask(body: Record<string, unknown>): Promise<FetchResult<{ ok: true; task: unknown }>> {
  return opsFetch(q("tasks"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function patchOpsTask(body: Record<string, unknown>): Promise<FetchResult<{ ok: true; task: unknown }>> {
  return opsFetch(q("tasks"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function postOpsFastfieldSync(): Promise<
  FetchResult<{
    ok: true;
    configured?: boolean;
    message?: string;
    webhook_url?: string;
    submissions?: FastfieldSubmissionLedgerRow[];
  }>
> {
  return opsFetch(q("fastfield-sync"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  });
}

export type FastfieldSubmissionLedgerRow = {
  id: string;
  organization_id: string;
  deal_id: string | null;
  external_submission_id: string;
  raw: Record<string, unknown>;
  created_at: string;
};

export async function listOpsFastfieldSubmissions(): Promise<
  FetchResult<{ ok: true; submissions: FastfieldSubmissionLedgerRow[] }>
> {
  return opsFetch(q("fastfield-submissions"));
}

export type OpsDailyChecklistConfigPayload = {
  ok: true;
  config: Record<string, unknown> | null;
  effective_source: "ops" | "legacy_org" | "none";
  ops_config: Record<string, unknown> | null;
  legacy_org_config: Record<string, unknown> | null;
};

export async function getOpsDailyChecklistConfig(): Promise<FetchResult<OpsDailyChecklistConfigPayload>> {
  return opsFetch(q("daily-checklist-config"));
}

export async function patchOpsDailyChecklistConfig(
  config: Record<string, unknown> | null
): Promise<FetchResult<{ ok: true; config: Record<string, unknown> | null; effective_source: string }>> {
  return opsFetch(q("daily-checklist-config"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sales_daily_checklist_config: config }),
  });
}
